import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { PageGallery } from "@/components/PageGallery";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import type { TranslationCountryLanding } from "@/content/translation-corpus/countries";

function faqsTh(c: TranslationCountryLanding) {
  return [
    { q: `แปลเอกสารไปใช้ที่${c.countryTh} ต้องใช้ผู้แปลระบบไหน?`, a: `${c.systemTh} — ตรวจทะเบียนผู้แปลได้ที่ ${c.registryTh}` },
    { q: `ลำดับขั้นตอนที่ถูกต้องคืออะไร?`, a: c.routeTh },
    { q: `ใช้กับเรื่องอะไรได้บ้าง?`, a: c.useCaseTh },
    { q: `สาเหตุที่ถูกตีกลับบ่อยคืออะไร?`, a: c.pitfallsTh.join(" · ") },
    { q: `ใช้เวลาเท่าไหร่?`, a: `${c.turnaroundTh} · ไม่รวมคิวของหน่วยงานปลายทาง` },
    {
      q: `${c.countryTh} เป็นภาคีอนุสัญญากรุงเฮก (Apostille) หรือไม่?`,
      a: c.hague
        ? `${c.countryTh} เป็นภาคีอนุสัญญากรุงเฮก แต่การใช้ Apostille ระหว่างสองประเทศต้องรอให้อนุสัญญามีผลกับไทยในวันที่ 28 กุมภาพันธ์ 2570 (2027) ก่อน ระหว่างนี้ยังใช้เส้นทางนิติกรณ์กรมการกงสุล + สถานทูตปลายทาง — ตรวจสถานะภาคีล่าสุดที่ hcch.net`
        : `ไม่เป็นภาคี — เอกสารจึงต้องใช้เส้นทางนิติกรณ์ (legalisation) ผ่านกรมการกงสุลและสถานเอกอัครราชทูตของ${c.countryTh} ต่อไป แม้หลังปี 2570 ก็ตาม ตรวจสถานะภาคีที่ hcch.net`,
    },
    {
      q: "การแปล การรับรองคำแปล การรับรองลายมือชื่อ และนิติกรณ์ ต่างกันอย่างไร?",
      a: "เป็นสี่ชั้นแยกกัน: (1) การแปลเนื้อหา (2) คำรับรองความถูกต้องของผู้แปล (3) การรับรองลายมือชื่อผู้ลงนามโดยทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร และ (4) นิติกรณ์/Apostille ที่รับรองตราประทับเพื่อใช้ข้ามประเทศ สามชั้นแรกไม่สามารถใช้แทนชั้นที่สี่ได้",
    },
    {
      q: "ควรแปลก่อนหรือทำนิติกรณ์ก่อน?",
      a: "ถ้าปลายทางกำหนดให้ผู้แปลในประเทศนั้นเป็นผู้แปล ให้ทำนิติกรณ์ที่ไทยก่อนแล้วจึงส่งแปล เพราะผู้แปลต้องถอดความตราประทับทั้งหมดลงในคำแปล ถ้าปลายทางรับคำแปลที่ทำในไทย ให้แปลและรับรองคำแปลก่อน แล้วนำคำแปลไปทำนิติกรณ์",
    },
    {
      q: "รับประกันได้ไหมว่าหน่วยงานปลายทางจะรับเอกสาร?",
      a: "การยอมรับเอกสารเป็นดุลพินิจของหน่วยงานผู้รับ เราจึงไม่รับประกันผล แต่จะยืนยันระบบรับรองและอายุเอกสารที่ปลายทางกำหนดก่อนเริ่มงาน และแก้ไขงานแปลให้ใหม่โดยไม่คิดค่าใช้จ่ายหากข้อผิดพลาดเกิดจากฝั่งเรา",
    },
  ];
}

export const Route = createFileRoute("/translation/country/$country")({
  loader: async ({ params }: { params: { country: string } }) => {
    const { TRANSLATION_COUNTRY_LANDINGS, getTranslationCountryLanding } = await import("@/content/translation-corpus/countries");
    const c = getTranslationCountryLanding(params.country);
    if (!c) throw notFound();
    return { c, siblings: TRANSLATION_COUNTRY_LANDINGS.filter((x) => x.slug !== c.slug).map((x) => ({ slug: x.slug, label: x.countryTh })) };
  },
  component: TranslationCountryPageTH,
  head: ({ params, loaderData }: { params: { country: string }; loaderData?: { c: TranslationCountryLanding } }) => {
    if (!loaderData) {
      return { meta: [{ title: "ไม่พบประเทศปลายทาง" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { c } = loaderData;
    const title = `แปลเอกสารไป${c.countryTh} — ระบบรับรองคำแปลและนิติกรณ์`;
    const description = `แปลเอกสารใช้ที่${c.countryTh}: ${c.systemTh} · ลำดับนิติกรณ์ที่ถูกต้อง สาเหตุที่ถูกตีกลับ และ ${c.turnaroundTh}`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: `/translation/country/${params.country}`,
        type: "article",
        alternates: [{ lang: "en", path: `/en/translation/country/${params.country}` }],
        keywords: [
          `แปลเอกสาร${c.countryTh}`,
          `แปลรับรอง${c.countryTh}`,
          `นิติกรณ์${c.countryTh}`,
          "รับรองคำแปลกรมการกงสุล",
          `certified translation ${c.countryEn}`,
        ],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: `${site}/` },
              { name: "แปลเอกสารรับรอง", url: `${site}/translation` },
              { name: `ปลายทาง ${c.countryTh}`, url: `${site}/translation/country/${params.country}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqsTh(c))) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/translation/country/${params.country}`)) },
      ],
    };
  },
});

function TranslationCountryPageTH() {
  const { c, siblings } = Route.useLoaderData();
  const faqs = faqsTh(c);
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/translation" className="hover:underline">แปลเอกสารรับรอง</a> <span>/</span>{" "}
          <span>{c.countryTh}</span>
        </nav>

        <h1 className="mb-3 text-3xl font-bold md:text-4xl">แปลเอกสารไปใช้ที่{c.countryTh}</h1>
        <p className="mb-6 text-lg text-muted-foreground" data-speakable="true">
          ระบบรับรองคำแปลที่ปลายทางกำหนด: <strong>{c.systemTh}</strong>
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-sm">
          <a href="/contact" className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground">ขอใบเสนอราคาฟรี →</a>
          <a href="/translation/faq" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">คำถามที่พบบ่อยงานแปล</a>
          <a href={`/en/translation/country/${c.slug}`} className="rounded-md border px-4 py-2 text-muted-foreground hover:bg-muted">English →</a>
        </div>

        <section className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="mb-3 text-xl font-bold">ระบบผู้แปลและทะเบียนที่ตรวจสอบได้</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{c.systemTh}</p>
          <p className="mt-3 text-sm text-muted-foreground"><strong>ทะเบียน/หน่วยงานอ้างอิง:</strong> {c.registryTh}</p>
          <p className="mt-3 text-sm text-muted-foreground">
            <strong>สถานะอนุสัญญากรุงเฮก:</strong>{" "}
            {c.hague ? "เป็นภาคี (ตรวจสถานะที่ hcch.net)" : "ไม่เป็นภาคี — ต้องใช้นิติกรณ์และสถานทูตต่อไป"}
          </p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">ลำดับขั้นตอนที่ถูกต้อง</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{c.routeTh}</p>
          <p className="mt-3 text-sm text-muted-foreground"><strong>ระยะเวลา:</strong> {c.turnaroundTh} (ไม่รวมคิวหน่วยงานปลายทาง)</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">ใช้กับเรื่องอะไร</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{c.useCaseTh}</p>
        </section>

        <section className="mb-10 rounded-lg border bg-muted/20 p-6">
          <h2 className="mb-3 text-xl font-bold">สาเหตุที่คำแปลถูกตีกลับ</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {c.pitfallsTh.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">คำถามที่พบบ่อย — แปลเอกสารไป{c.countryTh}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer text-sm font-semibold">{f.q}</summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">ประเทศปลายทางอื่น</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {siblings.map((x: { slug: string; label: string }) => (
              <a key={x.slug} href={`/translation/country/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.label}
              </a>
            ))}
          </div>
        </section>

        <p className="mb-6 text-xs text-muted-foreground">
          ข้อมูล ณ สิงหาคม 2569 (2026) — แหล่งอ้างอิงของหน้านี้: {c.sources.join(" · ")} · สถานะอนุสัญญากรุงเฮก hcch.net
          — ข้อกำหนดของหน่วยงานปลายทางเปลี่ยนแปลงได้ โปรดตรวจสอบก่อนดำเนินการ
        </p>
        <div className="mb-10">
          <PageGallery category="translation" seed="translation-country.country" locale="th" />
        </div>

      </main>
      <SiteFooter />
    </>
  );
}
